import React from "react";
import { ModalCompAdvance } from "../../../../components";
import { convertToTitleCase } from "../../../../utils";
import moment from "moment";
import { AiOutlineClose } from "react-icons/ai";
import { useGetSmsLogByIdQuery } from "../../../../store/admin/smsHistorySlice";
import {
  formatSourceType,
  getSourceTypeColor,
  getStatusBadgeColor,
  getStatusBadgeText,
} from "../../../../utils/smsHistoryUtils";

const SmsHistoryDetailsModal = ({ logId, onClose }) => {
  const {
    data: logData,
    isLoading,
    isError,
  } = useGetSmsLogByIdQuery(logId, {
    skip: !logId,
  });

  if (isLoading) {
    return (
      <ModalCompAdvance
        show={true}
        handleClose={onClose}
        size="xl"
        centered
        close={true}
        modalBody={0}
        arrayComp={[
          {
            component: (
              <div className="p-4 text-center">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3">Loading SMS details...</p>
              </div>
            ),
          },
        ]}
      />
    );
  }

  let log = logData;
  if (Array.isArray(logData) && logData.length > 0) {
    log = logData[0];
  }

  if (isError || !log || !log.id) {
    return (
      <ModalCompAdvance
        show={true}
        handleClose={onClose}
        size="xl"
        centered
        close={true}
        modalTitle={
          <div className="d-flex justify-content-between align-items-center w-100">
            <h5 className="fw-bold mb-0">SMS Details</h5>
            <AiOutlineClose
              onClick={onClose}
              className="cursor-pointer"
              size={24}
              style={{ color: "var(--color-primary)" }}
            />
          </div>
        }
        modalBody={0}
        arrayComp={[
          {
            component: (
              <div className="text-center py-5">
                <p className="text-danger">Unable to load SMS details.</p>
              </div>
            ),
          },
        ]}
      />
    );
  }

  const statusColor = getStatusBadgeColor(log.status);
  const sourceTypeColor = getSourceTypeColor(log.source_type);
  const metadata = log.metadata || {};

  return (
    <>
      <style>{`
        /* Target the modal dialog and content */
        body .modal.show .modal-dialog.modal-lg {
          max-width: 800px;
        }
        body .modal.show .modal-content {
          max-height: 85vh;
          display: flex;
          flex-direction: column;
        }
        /* Target modal body with our custom class */
        body .modal.show .modal-body.sms-details-modal-body {
          max-height: 70vh !important;
          overflow-y: auto !important;
          flex: 1;
          padding: 1rem 2rem !important;
        }
        /* Custom scrollbar styling for better UX */
        body .modal.show .modal-body.sms-details-modal-body::-webkit-scrollbar {
          width: 8px;
        }
        body .modal.show .modal-body.sms-details-modal-body::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }
        body .modal.show .modal-body.sms-details-modal-body::-webkit-scrollbar-thumb {
          background: var(--color-primary);
          border-radius: 4px;
        }
        body .modal.show .modal-body.sms-details-modal-body::-webkit-scrollbar-thumb:hover {
          background: color-mix(in srgb, var(--color-primary) 80%, black);
        }
      `}</style>
      <ModalCompAdvance
        show={true}
        handleClose={onClose}
        size="lg"
        centered
        close={true}
        designClass="sms-details-modal-body"
        modalTitle={
          <div className="d-flex justify-content-between align-items-center w-100">
            <h4 className="fw-bold mb-0" style={{ color: "var(--color-primary)" }}>
              SMS Details
            </h4>
            <AiOutlineClose
              onClick={onClose}
              className="cursor-pointer"
              size={24}
              style={{ color: "var(--color-primary)" }}
            />
          </div>
        }
        modalBody={0}
        arrayComp={[
          {
            component: (
              <div className="p-4">
              <div className="row g-4">
                {/* Recipient Information */}
                <div className="col-12">
                  <h6 className="fw-bold text-black2 mb-3">Recipient Information</h6>
                </div>
                <div className="col-md-6 border-bottom pb-3">
                  <p className="small text-muted mb-1">Recipient Name</p>
                  <p className="fw-bold text-black1 mb-0">
                    {log?.recipient_name || "N/A"}
                  </p>
                </div>
                <div className="col-md-6 border-bottom pb-3">
                  <p className="small text-muted mb-1">Recipient Phone</p>
                  <p className="fw-bold text-black1 mb-0" style={{ fontFamily: "monospace" }}>
                    {log?.recipient_phone || "N/A"}
                  </p>
                </div>
                <div className="col-md-6 border-bottom pb-3">
                  <p className="small text-muted mb-1">Recipient Type</p>
                  <p className="fw-bold text-black1 mb-0">
                    {convertToTitleCase(log?.recipient_type || "N/A")}
                  </p>
                </div>

                {/* Source Information */}
                <div className="col-12 mt-3">
                  <h6 className="fw-bold text-black2 mb-3">Source Information</h6>
                </div>
                <div className="col-md-6 border-bottom pb-3">
                  <p className="small text-muted mb-1">Source Type</p>
                  <span
                    className="px-3 py-1 rounded-pill text-white fw-semibold"
                    style={{
                      backgroundColor: sourceTypeColor,
                      fontSize: "12px",
                    }}
                  >
                    {formatSourceType(log?.source_type)}
                  </span>
                </div>
                <div className="col-md-6 border-bottom pb-3">
                  <p className="small text-muted mb-1">Source Name</p>
                  <p className="fw-bold text-black1 mb-0">
                    {log?.source_name || metadata?.event_name || metadata?.message_subject || metadata?.eminute_title || "N/A"}
                  </p>
                </div>
                {log?.source_id && (
                  <div className="col-md-6 border-bottom pb-3">
                    <p className="small text-muted mb-1">Source ID</p>
                    <p className="small fw-bold text-black1 mb-0" style={{ fontFamily: "monospace" }}>
                      {log.source_id}
                    </p>
                  </div>
                )}

                {/* Message Details */}
                <div className="col-12 mt-3">
                  <h6 className="fw-bold text-black2 mb-3">Message Details</h6>
                </div>
                <div className="col-12 border-bottom pb-3">
                  <p className="small text-muted mb-2">Full Message</p>
                  <div
                    className="p-3 rounded"
                    style={{
                      backgroundColor: "#fafafa",
                      border: "1px solid #e0e0e0",
                      maxHeight: "200px",
                      overflowY: "auto",
                    }}
                  >
                    <p className="mb-0 text-black1" style={{ whiteSpace: "pre-wrap" }}>
                      {log?.message || "N/A"}
                    </p>
                  </div>
                </div>
                <div className="col-md-6 border-bottom pb-3">
                  <p className="small text-muted mb-1">SMS Count</p>
                  <p className="fw-bold text-black1 mb-0">
                    {log?.sms_count || 1}
                  </p>
                </div>
                <div className="col-md-6 border-bottom pb-3">
                  <p className="small text-muted mb-1">Status</p>
                  <span
                    className="px-3 py-1 rounded-pill text-white fw-semibold"
                    style={{
                      backgroundColor: statusColor,
                      fontSize: "12px",
                    }}
                  >
                    {getStatusBadgeText(log?.status)}
                  </span>
                </div>

                {/* Error Message (if failed) */}
                {log?.error_message && (
                  <div className="col-12 border-bottom pb-3">
                    <p className="small text-danger fw-semibold mb-1">Error Message</p>
                    <p className="small text-danger mb-0">{log.error_message}</p>
                  </div>
                )}

                {/* Timestamps */}
                <div className="col-12 mt-3">
                  <h6 className="fw-bold text-black2 mb-3">Timestamps</h6>
                </div>
                {log?.sent_at && (
                  <div className="col-md-6 border-bottom pb-3">
                    <p className="small text-muted mb-1">Sent At</p>
                    <p className="small fw-bold text-black1 mb-0">
                      {moment(log.sent_at).format("DD MMMM, YYYY - h:mm A")}
                    </p>
                  </div>
                )}
                {log?.delivered_at && (
                  <div className="col-md-6 border-bottom pb-3">
                    <p className="small text-muted mb-1">Delivered At</p>
                    <p className="small fw-bold text-black1 mb-0">
                      {moment(log.delivered_at).format("DD MMMM, YYYY - h:mm A")}
                    </p>
                  </div>
                )}
                {log?.created_at && (
                  <div className="col-md-6 border-bottom pb-3">
                    <p className="small text-muted mb-1">Created At</p>
                    <p className="small fw-bold text-black1 mb-0">
                      {moment(log.created_at).format("DD MMMM, YYYY - h:mm A")}
                    </p>
                  </div>
                )}

                {/* Sent By */}
                {log?.sent_by && (
                  <div className="col-12 mt-3">
                    <h6 className="fw-bold text-black2 mb-3">Sent By</h6>
                  </div>
                )}
                {log?.sent_by?.name && (
                  <div className="col-md-6 border-bottom pb-3">
                    <p className="small text-muted mb-1">Staff Name</p>
                    <p className="fw-bold text-black1 mb-0">
                      {log.sent_by.name}
                    </p>
                  </div>
                )}
              </div>
              <div className="d-flex justify-content-end mt-4">
                <button
                  className="btn px-4 py-2 text-white rounded"
                  style={{ backgroundColor: "var(--color-primary)" }}
                  onClick={onClose}
                >
                  Close
                </button>
              </div>
            </div>
          ),
        },
      ]}
      />
    </>
  );
};

export default SmsHistoryDetailsModal;
